import type {
  ReadSearchApiArg,
  SearchFilterOperator,
  SearchFilterOperatorExistence,
} from 'lib-search-types';
import {
  filterFieldCatalogue,
  filterOperatorCatalogue,
  isExistenceFilterOperator,
  type SearchFilterChangeEvent,
  type SearchFilterItem,
} from 'lib-search-ui';
import { memo, useCallback, useMemo } from 'react';

import { SearchScopeFilter } from 'components/search';

import { useSearchQuickFilter } from '../../utils/useSearchQuickFilter';

const existenceOperatorItems: Record<
  SearchFilterOperatorExistence,
  SearchFilterItem
> = {
  is_empty: { value: 'is_empty', label: 'No queue' },
  is_not_empty: { value: 'is_not_empty', label: 'In queue' },
};

export const SearchQueueQuickFilter = memo(
  function SearchQueueQuickFilter(): JSX.Element {
    const { condition, handleChange } = useSearchQuickFilter('queue_id');

    const initialArg = useMemo(
      (): ReadSearchApiArg<'queues'> => ({
        scope: 'queues',
        filters: {
          strategy: 'all',
          filters: [
            {
              field: 'is_archived',
              operator: 'not_equals',
              values: [{ value: 'true' }],
            },
          ],
        },
        sortBy: { field: 'name', order: 'asc' },
      }),
      [],
    );

    const selectedValues = useMemo(() => {
      // When an existence operator is active, show its item as selected instead.
      if (
        condition?.operator &&
        isExistenceFilterOperator(condition.operator)
      ) {
        return [{ value: condition.operator }];
      }

      return condition?.values;
    }, [condition]);

    const selectedOperator = useMemo(() => {
      // Hide the existence operator from SearchFilter so the list stays enabled.
      if (
        condition?.operator &&
        isExistenceFilterOperator(condition.operator)
      ) {
        return undefined;
      }

      return condition?.operator;
    }, [condition]);

    /**
     * The last value in the array is the most recently clicked item. If it's an
     * existence operator item, the filter switches to that operator with empty
     * values. Otherwise, strips any existence items and forwards as normal.
     */
    const handleQueueChange = useCallback(
      (event: SearchFilterChangeEvent) => {
        const lastValue = event.values[event.values.length - 1];

        if (
          lastValue &&
          isExistenceFilterOperator(lastValue.value as SearchFilterOperator)
        ) {
          handleChange({
            action: 'upsert',
            operator: lastValue.value as SearchFilterOperator,
            values: [],
          });
        } else {
          handleChange({
            ...event,
            values: event.values.filter(
              (v) =>
                !isExistenceFilterOperator(v.value as SearchFilterOperator),
            ),
          });
        }
      },
      [handleChange],
    );

    return (
      <SearchScopeFilter
        scope="queues"
        placeholder="Search queues..."
        operators={filterOperatorCatalogue.conversations.queue_id.operators.filter(
          (o) => !isExistenceFilterOperator(o),
        )}
        defaultOperator={
          filterOperatorCatalogue.conversations.queue_id.defaultOperator
        }
        selectedValues={selectedValues}
        selectedOperator={selectedOperator}
        prependedItems={Object.values(existenceOperatorItems)}
        initialArg={initialArg}
        onChange={handleQueueChange}
      >
        {filterFieldCatalogue.conversations.queue_id.label}
      </SearchScopeFilter>
    );
  },
);
